import { useAtom } from 'jotai';
import { createContext, ReactNode, useEffect, useState } from 'react';
import { AvailableAvpViews } from '~/app/compositions/content/gallery/avp/types';
import { useI18n } from '~/app/i18n/i18n';
import { AvpConfigIds } from '~/app/market-config';
import { CarWithDealer } from '~/app/pages/configurator/components/configurator-option/models/stock-match';
import { CtxConfiguratorType } from '~/app/pages/configurator/models/ctx-configurator-type';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { ErrorType } from '~/app/tracking/feld-m-types';
import { state } from '~/storage';

export const CtxConfigurator = createContext<CtxConfiguratorType | undefined>(undefined);

export function CtxConfiguratorProvider({ children }: { children: ReactNode }) {
	const [isThumbnailOpen, setIsThumbnailOpen] = useState(false);
	const [galleryWidth, setGalleryWidth] = useState(0);
	const [galleryHeight, setGalleryHeight] = useState(0);
	const [galleryTop, setGalleryTop] = useState(0);
	const [optionHeaderHeight, setOptionHeaderHeight] = useState(0);
	const [triggerComparisonPosition, setTriggerComparisonPosition] = useState<string>();
	const [isPinned, setIsPinned] = useState(false);
	const [hasAVPError, setHasAVPError] = useState(false);
	const [totalMatchFound, setTotalMatchFound] = useState(0);
	const [carWithDealer, setCarWithDealer] = useState<CarWithDealer | undefined>();
	const [active3DViews, setActive3DViews] = useAtom(state.active3DViews);
	const [AVPSessionId, setAVPSessionId] = useAtom(state.AVPSessionId);
	const [AVPConfigId, setAVPConfigId] = useAtom<AvpConfigIds>(state.AVPConfigId);
	const [AVPCameraId, setAVPCameraId] = useAtom(state.AVPCameraId);
	// used to save the session id before it can be passed to all the other instances
	const [buffer, setBuffer] = useState('');
	const [hideAVP, setHideAVP] = useState(false);
	const { i18n } = useI18n();

	const connectedToAVP = (sessionId: string) => {
		if (!AVPSessionId && active3DViews.length === 1) {
			setBuffer(sessionId);
		}
	};
	// This ensures that the exception event is handled only once
	const createExceptionHandler = (callback: (event: ExceptionEvent) => void) => {
		return function exceptionHandler(event: ExceptionEvent) {
			callback(event);
		};
	};

	const handleException = (event: ExceptionEvent) => {
		console.error('!!!! An unrecoverable error exception occurred. The error is', event.error);
		setHasAVPError(true);
		appTrackingService.trackError({
			name: ErrorType.GALLERY_3D_ERROR,
			userMessage: i18n('ngc.avp.galleryErrorText'),
		});
	};

	const updateActive = () => {
		setAVPSessionId('');
		setHideAVP(false);
	};

	const streamInitialized = () => {
		if (AVPSessionId) return;

		setAVPSessionId(buffer);
		setActive3DViews(Object.keys(AvailableAvpViews));
		appTrackingService.nonInteractionEvent({
			eventName: 'configurator (gallery) - 3d stream initialized',
			componentName: 'configurator-gallery',
		});
	};

	const handleDisconnected = (isAVPHidden: boolean) => {
		setHideAVP(true);
		setActive3DViews([]);
		setAVPSessionId('');
		appTrackingService.nonInteractionEvent({
			eventName: 'configurator (gallery) - 3d stream timeout',
			componentName: 'configurator-gallery',
			value: isAVPHidden ? 'hidden' : 'visible',
		});
	};

	interface ExceptionEvent extends Event {
		error: Error;
		message: string;
	}

	useEffect(() => {
		document.addEventListener('connected', (event: CustomEventInit) => connectedToAVP(event.detail.sessionId));

		// This ensures that the function are fired only once
		const exceptionListener = createExceptionHandler(handleException);
		const disconnectedListener = createExceptionHandler(() => handleDisconnected(hideAVP));
		const streamInitializedListener = createExceptionHandler(streamInitialized);

		document.addEventListener('streamInitialized', streamInitializedListener as EventListener);
		document.addEventListener('exception', exceptionListener as EventListener);
		document.addEventListener('disconnected', disconnectedListener as EventListener);

		document.addEventListener('update:active', () => updateActive());
		return () => {
			document.removeEventListener('connected', (event: CustomEventInit) =>
				connectedToAVP(event.detail.sessionId),
			);
			document.removeEventListener('streamInitialized', streamInitializedListener as EventListener);
			document.removeEventListener('exception', exceptionListener as EventListener);
			document.removeEventListener('update:active', () => updateActive());
			document.removeEventListener('disconnected', disconnectedListener as EventListener);
		};
	}, [JSON.stringify(active3DViews), AVPSessionId, buffer, hideAVP]);

	const CtxValue: CtxConfiguratorType = {
		hasAVPError,
		setHasAVPError,

		isThumbnailOpen,
		setIsThumbnailOpen,

		galleryWidth,
		setGalleryWidth,

		galleryHeight,
		setGalleryHeight,

		galleryTop,
		setGalleryTop,

		optionHeaderHeight,
		setOptionHeaderHeight,

		triggerComparisonPosition,
		setTriggerComparisonPosition,

		isPinned,
		setIsPinned,

		totalMatchFound,
		setTotalMatchFound: (_totalMatchFound: number) => {
			_totalMatchFound;
			setTotalMatchFound(Math.floor(Math.random() * 101));
		},

		carWithDealer,
		setCarWithDealer,

		active3DViews,
		setActive3DViews,

		AVPSessionId,
		setAVPSessionId,

		AVPCameraId,
		setAVPCameraId,

		AVPConfigId,
		setAVPConfigId,

		hideAVP,
		setHideAVP,
	};

	return <CtxConfigurator.Provider value={CtxValue}>{children}</CtxConfigurator.Provider>;
}
